<script setup lang="ts">
/**
 * UiSkeleton - Design System Skeleton Loading Component
 *
 * A placeholder that mimics content layout during loading.
 *
 * WHEN TO USE SKELETON (vs Spinner vs Progress):
 * - Content loading where you know the layout (lists, cards, profiles)
 * - Initial page/section loads
 * - Image placeholders
 * - Perceived performance improvement (feels faster than spinner)
 *
 * VARIANT GUIDELINES:
 * - rectangle: Cards, images, buttons, generic blocks
 * - circle: Avatars, profile pictures, icons
 * - text: Paragraphs, titles, descriptions (multiple lines)
 *
 * DON'T USE SKELETON FOR:
 * - Brief operations (use Spinner)
 * - Known progress operations (use Progress)
 * - Buttons or interactive elements (use Spinner inside)
 *
 * @example
 * // User card skeleton
 * <div class="flex gap-3">
 *   <UiSkeleton variant="circle" size="48px" />
 *   <div class="flex-1">
 *     <UiSkeleton width="120px" height="16px" />
 *     <UiSkeleton variant="text" :lines="2" />
 *   </div>
 * </div>
 */
import { computed, ref, watch } from 'vue';

export interface UiSkeletonProps {
   /** Skeleton variant */
   variant?: 'rectangle' | 'circle' | 'text';
   /** Width (for rectangle) */
   width?: string;
   /** Height (for rectangle) */
   height?: string;
   /** Size (for circle - sets both width and height) */
   size?: string;
   /** Number of text lines */
   lines?: number;
   /** Border radius */
   rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
   /** Animation type */
   animation?: 'pulse' | 'wave' | 'none';
}

const props = withDefaults(defineProps<UiSkeletonProps>(), {
   variant: 'rectangle',
   width: '100%',
   height: '1rem',
   lines: 1,
   rounded: 'md',
   animation: 'pulse',
});

const roundedClasses = computed(() => {
   if (props.variant === 'circle')
      return 'rounded-full';
   const roundedMap = {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
   };
   return roundedMap[props.rounded];
});

const animationClasses = computed(() => {
   const animations = {
      pulse: 'animate-pulse',
      wave: 'animate-shimmer',
      none: '',
   };
   return animations[props.animation];
});

const rectangleStyle = computed(() => ({
   width: props.width,
   height: props.height,
}));

const circleStyle = computed(() => ({
   width: props.size || '3rem',
   height: props.size || '3rem',
}));

// Generate stable line widths once using a seeded pseudo-random approach
function generateLineWidths(lineCount: number): string[] {
   const widths: string[] = [];
   for (let i = 0; i < lineCount; i++) {
      // Use deterministic pattern based on line index for varied but stable widths
      const seed = ((i + 1) * 7) % 20; // Deterministic variation per line
      if (i === lineCount - 1) {
         // Last line is shorter (60-80%)
         widths.push(`${60 + seed}%`);
      }
      else {
         // Other lines are longer (85-100%)
         widths.push(`${85 + (seed * 0.75)}%`);
      }
   }
   return widths;
}

// Store widths in a stable ref, regenerate only when lines prop changes
const initialLineWidths = ref<string[]>(generateLineWidths(props.lines));

watch(() => props.lines, (newLines) => {
   initialLineWidths.value = generateLineWidths(newLines);
});

const textLineWidths = computed(() => initialLineWidths.value);
</script>

<template>
   <!-- Rectangle variant -->
   <div
      v-if="variant === 'rectangle'"
      class="bg-neutral-light"
      :class="[roundedClasses, animationClasses]"
      :style="rectangleStyle"
   />

   <!-- Circle variant -->
   <div
      v-else-if="variant === 'circle'"
      class="bg-neutral-light rounded-full"
      :class="animationClasses"
      :style="circleStyle"
   />

   <!-- Text variant -->
   <div v-else-if="variant === 'text'" class="space-y-2">
      <div
         v-for="(lineWidth, index) in textLineWidths"
         :key="index"
         class="h-4 bg-neutral-light rounded"
         :class="animationClasses"
         :style="{ width: lineWidth }"
      />
   </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.animate-shimmer {
  background: linear-gradient(
    90deg,
    theme('colors.neutral.light') 25%,
    theme('colors.neutral.DEFAULT') 50%,
    theme('colors.neutral.light') 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
</style>
